import { useNavigation, useSubmit } from "react-router";
import { redirect } from "react-router";
import {
  commitSession,
  commitTenantNudgeSession,
  getSession,
  getTenantNudgeSession,
} from "~/sessions";
import { TENANT_LOGOS, tenantFromRequest } from "~/tenants";
import { TenantSchema } from "~/gen/efibi/v1/hub/common/tenant_pb";
import { Code, ConnectError, createClient } from "@connectrpc/connect";
import { createBareTildevApiTransport } from "~/transport";
import {
  Button,
  FieldError,
  Form,
  Input,
  Label,
  TextField,
} from "react-aria-components";
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { SideContentLayout } from "~/ui/SideContentLayout";
import { parse, stringify } from "uuid";
import { IndeterminateSpinner } from "~/ui/IndeterminateSpinner";
import { HydraAdmin } from "~/http/hydra";
import { SyopeClient, SyopeError } from "~/http/syope";
import type { AuthFeatures } from "~/auth-features";
import { AUTH_FEATURES } from "~/auth-features";
import TenantSwitcher from "~/ui/TenantSwitcher";
import { z } from "zod";
import { parseRequestBody, useConformingForm } from "~/conform";
import { userClaims } from "../../users";
import type { Route } from "./+types/auth.sign-in";
import { AuthenticationService } from "~/gen/efibi/v1/syn/services/authentication_service_pb";
import { ProfileManagementService } from "~/gen/efibi/v1/rig/services/profile_management_pb";
import { fromJson, toJson } from "@bufbuild/protobuf";
import jwt_decode from "jwt-decode";

const actionSchema = z.discriminatedUnion("accountSource", [
  z.object({
    accountSource: z.literal("tildev"),
    tenantId: z.string().uuid(),
    username: z
      .string({
        required_error: "Le nom d'utilisateur doit être renseigné",
      })
      .trim()
      .min(1, "Le nom d'utilisateur doit être renseigné"),
    password: z
      .string({
        required_error: "Le mot de passe doit être renseigné",
      })
      .min(10, "Le mot de passe doit contenir au minimum 10 caractères"),
  }),
  z.object({
    accountSource: z.literal("syope"),
    tenantId: z.string().uuid(),
    username: z
      .string({
        required_error: "Le nom d'utilisateur doit être renseigné",
      })
      .trim()
      .min(1, "Le nom d'utilisateur doit être renseigné"),
    password: z
      .string({
        required_error: "Le mot de passe doit être renseigné",
      })
      .min(10, "Le mot de passe doit contenir au minimum 10 caractères"),
  }),
  z.object({
    accountSource: z.literal("syope-sso"),
    accessToken: z.string(),
    client: z.string(),
    uid: z.string(),
  }),
]);

function extractLoginChallenge(request: Request): string | null {
  return new URL(request.url).searchParams.get("login_challenge") || null;
}

function extractRedirectUrl(request: Request): string | null {
  return new URL(request.url).searchParams.get("url") || null;
}

export async function action({ request }: Route.ActionArgs) {
  const transport = createBareTildevApiTransport();
  const hydraAdmin = new HydraAdmin();
  const redirectUrl = extractRedirectUrl(request);
  const loginChallenge = extractLoginChallenge(request);
  const session = await getSession(request.headers.get("Cookie"));
  const tenantNudgeSession = await getTenantNudgeSession(
    request.headers.get("Cookie"),
  );

  const submission = await parseRequestBody(request, actionSchema);
  if (submission.status !== "success") {
    return submission.reply();
  }

  let authProviderToken = null;
  switch (submission.value.accountSource) {
    case "tildev":
      try {
        const { tenantId, username, password } = submission.value;
        const authSvc = createClient(AuthenticationService, transport);
        const response = await authSvc.authenticateUser({
          tenantId: parse(tenantId!) as Uint8Array,
          username,
          password,
        });
        tenantNudgeSession.set("tenantId", tenantId);
        authProviderToken = response.token;
        session.set("kind", "efibi");
      } catch (e) {
        if (e instanceof ConnectError && e.code == Code.NotFound) {
          return submission.reply({
            formErrors: [
              "L'identifiant et le mot de passe ne correspondent pas.",
            ],
          });
        }
        throw e;
      }
      break;
    case "syope":
      try {
        const { tenantId, username, password } = submission.value;
        let client = new SyopeClient(process.env.SYOPE_API_INTERNAL_URL!);
        const syopeCredentials = await client.signIn(username, password);
        tenantNudgeSession.set("tenantId", tenantId);
        session.set("kind", "syope");
        session.set("accessToken", syopeCredentials.accessToken);
        session.set("client", syopeCredentials.client);
        session.set("uid", syopeCredentials.uid);
        client = new SyopeClient(
          process.env.SYOPE_API_INTERNAL_URL!,
          syopeCredentials,
        );
        const user = await client.me();
        if (loginChallenge != null) {
          const hydraResponse = await hydraAdmin.acceptLoginRequest(
            loginChallenge,
            user.external_id,
          );
          return redirect(hydraResponse.redirect_to, {
            headers: [
              ["Set-Cookie", await commitSession(session)],
              [
                "Set-Cookie",
                await commitTenantNudgeSession(tenantNudgeSession),
              ],
            ],
          });
        }
        return redirect(
          redirectUrl ? "/" + decodeURIComponent(redirectUrl) : "/",
          {
            headers: [
              ["Set-Cookie", await commitSession(session)],
              [
                "Set-Cookie",
                await commitTenantNudgeSession(tenantNudgeSession),
              ],
            ],
          },
        );
      } catch (e) {
        console.error("Error communicating with Syope API", e);
        if (e instanceof SyopeError) {
          return submission.reply({
            formErrors: [e.message],
          });
        }
        throw e;
      }
      break;
    case "syope-sso":
      try {
        const { accessToken, client, uid } = submission.value;
        const syopeClient = new SyopeClient(
          process.env.SYOPE_API_INTERNAL_URL!,
          { accessToken, client, uid },
        );
        const user = await syopeClient.me();
        tenantNudgeSession.set("tenantId", user.organization_external_id);
        session.set("kind", "syope");
        session.set("accessToken", accessToken);
        session.set("client", client);
        session.set("uid", uid);
        if (loginChallenge != null) {
          const hydraResponse = await hydraAdmin.acceptLoginRequest(
            loginChallenge,
            user.external_id,
          );
          return redirect(hydraResponse.redirect_to, {
            headers: [
              ["Set-Cookie", await commitSession(session)],
              [
                "Set-Cookie",
                await commitTenantNudgeSession(tenantNudgeSession),
              ],
            ],
          });
        }
        return redirect(
          redirectUrl ? "/" + decodeURIComponent(redirectUrl) : "/",
          {
            headers: [
              ["Set-Cookie", await commitSession(session)],
              [
                "Set-Cookie",
                await commitTenantNudgeSession(tenantNudgeSession),
              ],
            ],
          },
        );
      } catch (e) {
        if (e instanceof SyopeError) {
          return submission.reply({
            formErrors: [e.message],
          });
        }
        throw e;
      }
      break;
    default:
      return submission.reply({
        formErrors: ["La méthode de connexion n'est pas reconnue."],
      });
  }

  // TODO: allow profile-less sessions ?
  try {
    const profileSvc = createClient(ProfileManagementService, transport);
    const profileResponse = await profileSvc.listUserProfiles({
      authenticationProviderToken: authProviderToken,
    });
    if (profileResponse.profiles.length === 0) {
      return submission.reply({
        formErrors: [
          "Aucun profil n'est associé à votre compte. Contactez votre administrateur.",
        ],
      });
    } else if (profileResponse.profiles.length === 1) {
      const sessionResponse = await profileSvc.exchangeAuthenticationToken({
        authenticationProviderToken: authProviderToken,
        profileId: profileResponse.profiles[0].id,
      });
      session.set("accessToken", sessionResponse.accessToken);
      if (loginChallenge != null) {
        const claims = userClaims(sessionResponse.accessToken);
        const hydraResponse = await hydraAdmin.acceptLoginRequest(
          loginChallenge,
          claims.sub,
        );
        return redirect(hydraResponse.redirect_to, {
          headers: [
            ["Set-Cookie", await commitSession(session)],
            ["Set-Cookie", await commitTenantNudgeSession(tenantNudgeSession)],
          ],
        });
      }
      return redirect(
        redirectUrl ? "/" + decodeURIComponent(redirectUrl) : "/",
        {
          headers: [
            ["Set-Cookie", await commitSession(session)],
            ["Set-Cookie", await commitTenantNudgeSession(tenantNudgeSession)],
          ],
        },
      );
    } else {
      session.set("authProviderToken", authProviderToken);
      let suffix = "";
      if (loginChallenge != null) {
        suffix = `?login_challenge=${loginChallenge}`;
      } else if (
        redirectUrl != null &&
        decodeURIComponent(redirectUrl) !== "/"
      ) {
        suffix = `?url=${decodeURIComponent(redirectUrl)}`;
      }
      return redirect(`/auth/profile${suffix}`, {
        headers: [
          ["Set-Cookie", await commitSession(session)],
          ["Set-Cookie", await commitTenantNudgeSession(tenantNudgeSession)],
        ],
      });
    }
  } catch (e) {
    console.error(e);
    return submission.reply({
      formErrors: ["Une erreur inattendue est survenue."],
    });
  }
}

export async function loader({ request }: Route.LoaderArgs) {
  const hydraAdmin = new HydraAdmin();
  const loginChallenge = extractLoginChallenge(request);
  const redirectUrl = extractRedirectUrl(request);
  const session = await getSession(request.headers.get("Cookie"));
  if (
    session.has("accessToken") &&
    (session.get("accessToken") as string).length > 0
  ) {
    if (loginChallenge != null) {
      if (session.get("kind") === "efibi") {
        const claims = userClaims(session.get("accessToken"));
        const hydraResponse = await hydraAdmin.acceptLoginRequest(
          loginChallenge,
          claims.sub,
        );
        return redirect(hydraResponse.redirect_to);
      } else {
        const accessToken = session.get("accessToken");
        const client = session.get("client");
        const uid = session.get("uid");
        const syopeClient = new SyopeClient(
          process.env.SYOPE_API_INTERNAL_URL!,
          {
            accessToken,
            client,
            uid,
          },
        );
        const user = await syopeClient.me();
        const hydraResponse = await hydraAdmin.acceptLoginRequest(
          loginChallenge,
          user.external_id,
        );
        return redirect(hydraResponse.redirect_to);
      }
    } else {
      return redirect(
        redirectUrl ? "/" + decodeURIComponent(redirectUrl) : "/",
      );
    }
  }
  const tenant = await tenantFromRequest(request);
  let authFeatures: AuthFeatures = {
    password: null,
    syopeOidcProviders: [],
  };
  let tenantLogo = "/logo-syope.png";
  let tenantSerialized = null;
  if (tenant != null) {
    tenantSerialized = toJson(TenantSchema, tenant);
    const tenantId = stringify(tenant.id);
    if (AUTH_FEATURES.has(tenantId)) {
      authFeatures = AUTH_FEATURES.get(stringify(tenant.id))!;
    }
    if (TENANT_LOGOS.has(tenantId)) {
      tenantLogo = TENANT_LOGOS.get(tenantId)!;
    }
  }
  return { tenant: tenantSerialized, authFeatures, tenantLogo };
}

export default function SignIn({ loaderData }: Route.ComponentProps) {
  const { authFeatures, tenantLogo } = loaderData;
  let tenant = null;
  let tenantId = null;
  if (loaderData.tenant != null) {
    tenant = fromJson(TenantSchema, loaderData.tenant);
    tenantId = stringify(tenant.id);
  }
  const [form, fields] = useConformingForm(actionSchema);
  const submit = useSubmit();
  const navigation = useNavigation();
  const isSubmitting = navigation.formAction === "/auth/sign-in";
  const handleSsoAuthenticate = (creds: SsoCredentials) => {
    const formData = new FormData();
    formData.set("accountSource", "syope-sso");
    formData.set("accessToken", creds.accessToken);
    formData.set("client", creds.client);
    formData.set("uid", creds.uid);
    formData.set("expiry", creds.expiry.toString());
    submit(formData, { method: "post" });
  };

  return (
    <SideContentLayout coverUrl="https://images.unsplash.com/photo-1496917756835-20cb06e75b4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1908&q=80">
      <div>
        <img
          className="h-10 w-auto"
          src="/logo-tildev-black.svg"
          alt="Tildev"
        />
        <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
          Connexion
        </h2>
      </div>

      <TenantSwitcher tenantLogo={tenantLogo} tenant={tenant} />

      <Form
        method="post"
        id={form.id}
        validationErrors={form.allErrors}
        onSubmit={form.onSubmit}
      >
        <input type="hidden" name="tenantId" value={tenantId ?? ""} />

        {form.errors != null && (
          <div className="mt-10 p-2 rounded border-red-400 ring-1 ring-red-900 ring-opacity-30 text-sm font-semibold bg-red-100 text-red-600">
            <p>{form.errors}</p>
          </div>
        )}

        {authFeatures.syopeOidcProviders.length > 0 && (
          <div className="mt-10 space-y-4">
            {authFeatures.syopeOidcProviders.map((p) => (
              <div key={p.key} className="grid grid-cols-1 gap-4">
                <ButtonSso
                  ssoKey={p.key}
                  onAuthenticate={handleSsoAuthenticate}
                >
                  Connexion via {p.label}
                </ButtonSso>
              </div>
            ))}

            {authFeatures.password != null && (
              <div className="mt-6 relative">
                <div
                  className="absolute inset-0 flex items-center"
                  aria-hidden="true"
                >
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm font-medium leading-6">
                  <span className="bg-white px-6 text-gray-900">
                    Ou saisissez vos identifiants
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {authFeatures.password != null && (
          <div className="mt-6">
            <div className="space-y-6">
              <TextField
                name={fields.username.name}
                isDisabled={isSubmitting}
                isRequired={fields.username.required}
              >
                <Label className="block text-sm font-medium leading-6 text-gray-900">
                  Nom d'utilisateur
                </Label>
                <div className="mt-2">
                  <Input className="block w-full rounded-md border-0 p-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" />
                </div>
                <FieldError className="mt-2 text-sm text-red-600" />
              </TextField>

              <TextField
                name={fields.password.name}
                type="password"
                isDisabled={isSubmitting}
                isRequired={fields.password.required}
              >
                <Label className="block text-sm font-medium leading-6 text-gray-900">
                  Mot de passe
                </Label>
                <div className="mt-2">
                  <Input className="block w-full rounded-md border-0 p-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" />
                </div>
                <FieldError className="mt-2 text-sm text-red-600" />
              </TextField>

              <div className="flex items-center justify-between">
                <div className="flex items-center"></div>

                <div className="text-sm leading-6">
                  <a
                    href="#"
                    className="font-semibold text-blue-600 hover:text-blue-500"
                  >
                    Mot de passe oublié ?
                  </a>
                </div>
              </div>

              <div>
                <input
                  type="hidden"
                  name={fields.accountSource.name}
                  value={authFeatures.password}
                />

                <Button
                  type="submit"
                  isDisabled={isSubmitting}
                  className="min-h-9 flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                >
                  {!isSubmitting && "Connexion"}
                  {isSubmitting && <IndeterminateSpinner className="h-6 w-6" />}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Form>
    </SideContentLayout>
  );
}

export interface SsoCredentials {
  accessToken: string;
  expiry: number;
  client: string;
  uid: string;
}

export interface ButtonSsoProps {
  ssoKey: string;
  onAuthenticate?: (ssoCredentials: SsoCredentials) => unknown;
}

export function ButtonSso({
  ssoKey,
  onAuthenticate = () => {},
  children,
}: PropsWithChildren<ButtonSsoProps>) {
  const [popup, setPopup] = useState<Window | null>(null);
  const handleMessage = useCallback(
    (ev: MessageEvent) => {
      console.log("received...");
      const credentials = {
        accessToken: ev.data["access-token"],
        expiry: ev.data.expiry,
        uid: ev.data.uid,
        client: ev.data.client,
      };
      onAuthenticate(credentials);
      popup?.close();
    },
    [onAuthenticate, popup],
  );
  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);
  const handleClick = () => {
    const popupWindow = window.open(
      // @ts-expect-error La globale d'environnement n'est pas connue par tsc
      `${window.ENV.SYOPE_API_ORIGIN}/omniauth/${ssoKey}?retctx=moncompte`,
      "_blank",
      "width=480,height=520",
    );
    if (popupWindow) {
      popupWindow.onclose = () => {
        setPopup(null);
      };
      setPopup(popupWindow);
    } else {
      console.error("SSO popup window got blocked.");
    }
  };

  return (
    <Button
      onPress={handleClick}
      isDisabled={popup != null}
      className="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-transparent"
    >
      <span className="text-sm font-semibold leading-6">{children}</span>
    </Button>
  );
}
